import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { parseISO } from 'date-fns';
import {
  CheckpointService,
  RepairTaskDetailDto,
  RepairTaskMergeDto,
  RepairTaskService,
  UserDetailDto,
} from '@artemis-software/wr-api';
import { CurrentUserService } from '@services/current-user.service';
import { RepairTaskDispatchService } from '@services/repair-task-dispatch.service';
import { isClosedRepairTaskStatus } from '@services/repair-task-status-change.service';
import { GenericDialogComponent } from '@components/dialog/generic-dialog/generic-dialog.component';
import {
  openRepairTaskCloseDialog,
  RepairTaskCloseMode,
} from '@components/dialog/repair-task-close-dialog/repair-task-close-dialog.component';
import StatusEnum = RepairTaskDetailDto.StatusEnum;
import TenantTypeEnum = UserDetailDto.TenantTypeEnum;

export interface RepairTaskCreateContext {
  buildingId: string;
  inspectionEntryId: string;
  checkpointId: string;
  title?: string;
  description?: string;
}

interface ContractorOption {
  id: string;
  name: string;
}

export interface RepairTaskEditOutcome {
  task: RepairTaskDetailDto | null;
}

export async function openRepairTaskEditDialogForOutcome(
  dialogService: NbDialogService,
  repairTaskId: string,
): Promise<RepairTaskEditOutcome | undefined> {
  const ref = dialogService.open(RepairTaskEditDialogComponent, {
    autoFocus: false,
    closeOnBackdropClick: false,
    context: { repairTaskId },
  });
  const outcome: RepairTaskEditOutcome | false | undefined = await firstValueFrom(ref.onClose);
  return outcome || undefined;
}

export async function openRepairTaskEditDialog(dialogService: NbDialogService, repairTaskId: string): Promise<boolean> {
  return !!(await openRepairTaskEditDialogForOutcome(dialogService, repairTaskId));
}

export async function openRepairTaskCreateDialog(
  dialogService: NbDialogService,
  createContext: RepairTaskCreateContext,
): Promise<boolean> {
  const ref = dialogService.open(RepairTaskEditDialogComponent, {
    autoFocus: false,
    closeOnBackdropClick: false,
    context: { createContext },
  });
  return !!(await firstValueFrom(ref.onClose));
}

@Component({
  standalone: false,
  selector: 'wr-repair-task-edit-dialog',
  templateUrl: './repair-task-edit-dialog.component.html',
  styleUrls: ['./repair-task-edit-dialog.component.scss'],
})
export class RepairTaskEditDialogComponent implements OnInit {
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly checkpointService = inject(CheckpointService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly dispatchService = inject(RepairTaskDispatchService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);
  private readonly dialogRef = inject(NbDialogRef<RepairTaskEditDialogComponent>);

  @Input() repairTaskId?: string;
  @Input() createContext?: RepairTaskCreateContext;

  readonly selectableStatuses = Object.values(StatusEnum).filter(status => !isClosedRepairTaskStatus(status));

  readonly repairTask = signal<RepairTaskDetailDto | undefined>(undefined);
  readonly contractorOptions = signal<ContractorOption[]>([]);
  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly changed = signal(false);

  readonly isCreate = computed(() => !this.repairTaskId);
  readonly canEdit = computed(() => this.isCreate() || !!this.repairTask()?.permissions.canEdit);
  readonly canFinish = computed(() => this.canCloseAs(StatusEnum.Finished));
  readonly canDecline = computed(() => this.canCloseAs(StatusEnum.Declined));
  readonly buildingId = computed(() => this.repairTask()?.building?.id ?? this.createContext?.buildingId);
  readonly header = computed(() => this.isCreate() ? 'Reparaturauftrag erstellen' : 'Reparaturauftrag');

  readonly form = new FormGroup({
    title: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    description: new FormControl('', { nonNullable: true }),
    deadline: new FormControl<Date | null>(null),
    contractorId: new FormControl<string | null>(null),
    status: new FormControl<StatusEnum>(StatusEnum.Todo, { nonNullable: true }),
  });

  readonly cancelResponse = (): RepairTaskEditOutcome | false =>
    this.changed() ? { task: this.repairTask() ?? null } : false;

  readonly submitResponse = async (): Promise<RepairTaskEditOutcome | false> => {
    if (this.form.invalid || this.busy()) {
      return false;
    }
    const saved = await this.save();
    return saved ? { task: saved } : false;
  };

  async ngOnInit(): Promise<void> {
    try {
      if (this.repairTaskId) {
        const task = await firstValueFrom(this.repairTaskService.findById(this.repairTaskId));
        this.applyTask(task);
      } else if (this.createContext) {
        this.form.controls.contractorId.addValidators(Validators.required);
        this.form.patchValue({
          title: this.createContext.title ?? '',
          description: this.createContext.description ?? '',
        });
      }
      await this.loadContractorOptions();
    } finally {
      this.loading.set(false);
    }
  }

  statusPlaceholder(): string {
    const status = this.repairTask()?.status;
    if (status === StatusEnum.Finished) {
      return 'Abgeschlossen';
    }
    if (status === StatusEnum.Declined) {
      return 'Storniert';
    }
    return 'Status wählen';
  }

  async close(mode: RepairTaskCloseMode): Promise<void> {
    const task = this.repairTask();
    if (!task) {
      return;
    }
    const result = await openRepairTaskCloseDialog(this.dialogService, mode, task.title, this.isContractor());
    if (!result) {
      return;
    }
    await this.runBusy(async () => {
      const updated = mode === 'finish'
        ? await firstValueFrom(this.repairTaskService.finish(task.id, result))
        : await firstValueFrom(this.repairTaskService.decline(task.id, { comment: result.comment }));
      this.repairTask.set(updated);
      this.form.controls.status.setValue(updated.status);
      this.changed.set(true);
      this.toastrService.success(
        mode === 'finish' ? 'Reparaturauftrag abgeschlossen' : 'Reparaturauftrag storniert',
        'Reparaturauftrag',
      );
    });
  }

  async resend(): Promise<void> {
    const task = this.repairTask();
    if (!task) {
      return;
    }
    if (this.form.dirty && !(await this.save())) {
      return;
    }
    const sent = await this.dispatchService.sendMail(task.id);
    if (sent) {
      this.repairTask.set(sent);
      this.form.controls.status.setValue(sent.status);
      this.changed.set(true);
    }
  }

  async delete(): Promise<void> {
    const task = this.repairTask();
    if (!task) {
      return;
    }
    const confirmed = await firstValueFrom(this.dialogService.open(GenericDialogComponent, {
      context: {
        header: 'Reparaturauftrag löschen',
        message: `Soll der Reparaturauftrag „${task.title}“ gelöscht werden?`,
        status: 'danger',
      },
    }).onClose);
    if (!confirmed) {
      return;
    }
    await this.runBusy(async () => {
      await firstValueFrom(this.repairTaskService.deleteById(task.id));
      this.toastrService.success('Reparaturauftrag gelöscht', 'Reparaturauftrag');
      this.dialogRef.close({ task: null } satisfies RepairTaskEditOutcome);
    });
  }

  private async save(): Promise<RepairTaskDetailDto | undefined> {
    this.busy.set(true);
    try {
      const saved = await firstValueFrom(this.repairTaskService.merge(this.toMergeDto()));
      this.applyTask(saved);
      this.changed.set(true);
      this.toastrService.success('Reparaturauftrag gespeichert', 'Reparaturauftrag');
      return saved;
    } catch {
      return undefined;
    } finally {
      this.busy.set(false);
    }
  }

  private toMergeDto(): RepairTaskMergeDto {
    const { title, description, deadline, contractorId, status } = this.form.getRawValue();
    const task = this.repairTask();
    return {
      id: task?.id,
      title: title.trim(),
      description: description.trim(),
      deadline: deadline?.toISOString(),
      clearDeadline: !deadline,
      contractorId: contractorId ?? undefined,
      status: status === task?.status ? undefined : status,
      inspectionEntryId: this.createContext?.inspectionEntryId,
      buildingEquipmentId: undefined,
      buildingId: this.createContext?.buildingId,
    };
  }

  private applyTask(task: RepairTaskDetailDto): void {
    this.repairTask.set(task);
    this.form.reset({
      title: task.title,
      description: task.description,
      deadline: task.deadline ? parseISO(task.deadline) : null,
      contractorId: task.contractor?.id ?? null,
      status: task.status,
    });
    if (task.permissions.canEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  private async loadContractorOptions(): Promise<void> {
    if (!this.canEdit()) {
      return;
    }
    const task = this.repairTask();
    const options = new Map<string, ContractorOption>();
    const add = (option: ContractorOption) => options.set(option.id, { id: option.id, name: option.name });

    if (task?.contractor) {
      add(task.contractor);
    }
    for (const option of await this.buildingContractorOptions(task)) {
      add(option);
    }
    const checkpointId = this.createContext?.checkpointId ?? task?.inspectionEntryCheckpoint?.id;
    if (checkpointId && this.isInternal()) {
      const checkpoint = await firstValueFrom(this.checkpointService.findById(checkpointId));
      checkpoint.contractors.forEach(add);
    }
    this.contractorOptions.set([...options.values()].sort((a, b) => a.name.localeCompare(b.name)));
  }

  private async buildingContractorOptions(task: RepairTaskDetailDto | undefined): Promise<ContractorOption[]> {
    if (!this.currentUserService.hasPrivilege('canEditRepairTasks')) {
      return [];
    }
    if (task?.buildingEquipment && !task.inspectionEntry) {
      return firstValueFrom(this.repairTaskService.findEquipmentContractorOptions(task.buildingEquipment.id));
    }
    const buildingId = task?.building?.id ?? this.createContext?.buildingId;
    return buildingId ? firstValueFrom(this.repairTaskService.findContractorOptions(buildingId)) : [];
  }

  private canCloseAs(target: StatusEnum): boolean {
    const task = this.repairTask();
    if (!task?.permissions.canClose || task.status === target) {
      return false;
    }
    return !this.isContractor() || task.status === StatusEnum.Todo || task.status === StatusEnum.InProgress;
  }

  private isInternal(): boolean {
    return this.currentUserService.currentUser()?.tenantType === TenantTypeEnum.Internal;
  }

  private isContractor(): boolean {
    return this.currentUserService.currentUser()?.tenantType === TenantTypeEnum.Contractor;
  }

  private async runBusy(action: () => Promise<void>): Promise<void> {
    this.busy.set(true);
    try {
      await action();
    } catch {
      this.changed.set(true);
    } finally {
      this.busy.set(false);
    }
  }
}
